import {
  ArrowDownIcon,
  ArrowUpIcon,
  CheckIcon,
  MinusCircleIcon,
  ProhibitIcon,
  ShieldWarningIcon,
  XIcon,
} from '@phosphor-icons/react'
import { mappedColors, typography } from '@pixee/design-system'
import { match } from 'ts-pattern'
import type {
  BlockedFixOutcome,
  BlockedScaOutcome,
  BlockedTriageOutcome,
  CompletedScaOutcome,
  CompletedTriageOutcome,
  ExcludedFixOutcome,
  ExcludedScaOutcome,
  ExcludedTriageOutcome,
  FailedFixOutcome,
  FailedScaOutcome,
  FailedTriageOutcome,
  NoRecommendationsFixOutcome,
  NoRecommendationsTriageOutcome,
  OutOfScopeFixOutcome,
  PushResponse,
} from '../../utils/api-client/api-schemas.ts'
import { PushStatusIcon } from '../badges/push-status-badge.tsx'
import { CopyButton } from '../buttons/copy-button.tsx'
import { getScaClassificationLabel, getSuggestedStatusLabel } from '../tables/scan-findings-table.tsx'
import { TooltipWithHover } from '../tooltip.tsx'
import * as styles from './outcome-banner.css.ts'

export type OutcomeBannerOutcome =
  | CompletedTriageOutcome
  | FailedTriageOutcome
  | NoRecommendationsTriageOutcome
  | BlockedTriageOutcome
  | ExcludedTriageOutcome
  | FailedFixOutcome
  | NoRecommendationsFixOutcome
  | BlockedFixOutcome
  | OutOfScopeFixOutcome
  | ExcludedFixOutcome
  | CompletedScaOutcome
  | FailedScaOutcome
  | BlockedScaOutcome
  | ExcludedScaOutcome

type OutcomeBannerProps = {
  outcome: OutcomeBannerOutcome
  copyText?: string
  copyButtonTooltip?: string
  onPush?: () => unknown
  isPushInProgress?: boolean
  pushIntegrationName?: string
  pushState?: PushResponse
}

const DEFAULT_COPY_LABEL = 'Copy summary'

type BannerVariant = 'spark' | 'solar' | 'drift' | 'nebula' | 'void'

const getBannerVariant = (outcome: OutcomeBannerOutcome): BannerVariant =>
  match(outcome)
    .with({ type: 'completed-triage' }, ({ suggested_status }) =>
      match(suggested_status)
        .with('true_positive', () => 'spark' as const)
        .with('suspicious', () => 'solar' as const)
        .with('inconclusive', () => 'drift' as const)
        .with('false_positive', 'wont_fix', () => 'nebula' as const)
        .otherwise(() => 'void' as const)
    )
    .with({ type: 'completed-sca' }, ({ classification }) =>
      match(classification)
        .with('exploitable', () => 'spark' as const)
        .with('inconclusive', 'potentially-exploitable', () => 'drift' as const)
        .with('not-exploitable', () => 'nebula' as const)
        .exhaustive()
    )
    .with({ type: 'no-recommendations-triage' }, () => 'nebula' as const)
    .with({ type: 'no-recommendations-fix' }, () => 'nebula' as const)
    .with(
      { type: 'failed-triage' },
      { type: 'blocked-triage' },
      { type: 'excluded-triage' },
      { type: 'failed-fix' },
      { type: 'blocked-fix' },
      { type: 'out-of-scope-fix' },
      { type: 'excluded-fix' },
      { type: 'failed-sca' },
      { type: 'blocked-sca' },
      { type: 'excluded-sca' },
      () => 'void' as const
    )
    .exhaustive()

/**
 * Text representation of the metadata shown in the banner header (classification,
 * Pixee severity, etc.), so a tab's copy button can include the same summary the
 * user sees above the content. Returns an empty string for outcomes without a
 * copy button (fix outcomes).
 */
export const getOutcomeBannerCopyText = (outcome: OutcomeBannerOutcome): string =>
  match(outcome)
    .with({ type: 'completed-triage' }, ({ suggested_status, suggested_severity }) =>
      [
        `Classification\n${getSuggestedStatusLabel(suggested_status)}`,
        suggested_severity ? `Pixee severity\n${suggested_severity.label}` : undefined,
      ]
        .filter(Boolean)
        .join('\n\n')
    )
    .with(
      { type: 'completed-sca' },
      ({ classification }) => `Classification\n${getScaClassificationLabel(classification)}`
    )
    .with({ type: 'failed-triage' }, { type: 'failed-sca' }, () => 'Classification\nError')
    .with({ type: 'blocked-triage' }, { type: 'blocked-sca' }, () => 'Classification\nBlocked')
    .with({ type: 'excluded-triage' }, { type: 'excluded-sca' }, () => 'Classification\nExcluded')
    .with({ type: 'no-recommendations-triage' }, () => 'No recommendations')
    .with(
      { type: 'failed-fix' },
      { type: 'blocked-fix' },
      { type: 'out-of-scope-fix' },
      { type: 'excluded-fix' },
      { type: 'no-recommendations-fix' },
      () => ''
    )
    .exhaustive()

const SimpleBanner: React.FC<{ label: string; icon: React.ReactNode; variant: BannerVariant }> = ({
  label,
  icon,
  variant,
}) => (
  <div className={styles.banner({ variant, layout: 'centered' })}>
    <span className={styles.statusLabel}>{label}</span>
    {icon}
  </div>
)

const ClassificationBanner: React.FC<{
  label: string
  icon: React.ReactNode
  copyText?: string
  copyButtonTooltip?: string
}> = ({ label, icon, copyText, copyButtonTooltip }) => (
  <div className={styles.banner({ variant: 'void' })}>
    <div className={styles.metadataGroup}>
      <span className={styles.metadataLabel}>Classification</span>
      <span className={styles.metadataValue}>{label}</span>
      {icon}
    </div>
    <div className={styles.copyButtonContainer}>
      <CopyButton
        copyText={copyText ?? ''}
        tooltipText={copyButtonTooltip ?? DEFAULT_COPY_LABEL}
        label={copyButtonTooltip ?? DEFAULT_COPY_LABEL}
        size="xlarge"
      />
    </div>
  </div>
)

const SeverityIcon: React.FC<{ rank: number }> = ({ rank }) => {
  if (rank >= 75) {
    return <ArrowUpIcon className={styles.severityIcon} weight="light" />
  }
  if (rank <= 25 && rank > 0) {
    return <ArrowDownIcon className={styles.severityIcon} weight="light" />
  }
  return <CheckIcon className={styles.severityIcon} weight="light" />
}

const TriageCompletedBanner: React.FC<{
  outcome: CompletedTriageOutcome
  copyText?: string
  copyButtonTooltip?: string
  onPush?: () => unknown
  isPushInProgress?: boolean
  pushIntegrationName?: string
  pushState?: PushResponse
}> = ({ outcome, copyText, copyButtonTooltip, onPush, isPushInProgress, pushIntegrationName, pushState }) => {
  const variant = getBannerVariant(outcome)
  const statusLabel = getSuggestedStatusLabel(outcome.suggested_status)

  return (
    <div className={styles.banner({ variant })}>
      <div className={styles.metadataGroup}>
        <span className={styles.metadataLabel}>Classification</span>
        <span className={styles.metadataValue}>{statusLabel}</span>
      </div>
      {outcome.suggested_severity && (
        <div className={styles.metadataGroup}>
          <span className={styles.metadataLabel}>Pixee severity</span>
          <span className={styles.metadataValue} style={{ textTransform: 'capitalize' }}>
            {outcome.suggested_severity.label}
          </span>
          <SeverityIcon rank={outcome.suggested_severity.rank} />
        </div>
      )}
      <div className={styles.copyButtonContainer}>
        <CopyButton
          copyText={copyText ?? ''}
          tooltipText={copyButtonTooltip ?? DEFAULT_COPY_LABEL}
          label={copyButtonTooltip ?? DEFAULT_COPY_LABEL}
          size="xlarge"
        />
        {onPush && (
          <TooltipWithHover
            side="top"
            trigger={
              <button
                type="button"
                onClick={onPush}
                disabled={isPushInProgress || pushState?.status === 'SUCCESS'}
                aria-label={match(pushState?.status)
                  .with('SUCCESS', () => (pushIntegrationName ? `Pushed to ${pushIntegrationName}` : 'Pushed'))
                  .with('IN_PROGRESS', () =>
                    pushIntegrationName ? `Push to ${pushIntegrationName} in progress` : 'Push in progress'
                  )
                  .with('FAILED', () => 'Push failed')
                  .otherwise(() =>
                    pushIntegrationName ? `Push classification to ${pushIntegrationName}` : 'Push classification'
                  )}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: isPushInProgress ? 'not-allowed' : 'pointer',
                  padding: '4px',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: isPushInProgress ? 0.5 : 1,
                }}
              >
                <PushStatusIcon pushState={pushState} size={20} />
              </button>
            }
          >
            {match(pushState?.status)
              .with('SUCCESS', () => (pushIntegrationName ? `Pushed to ${pushIntegrationName}` : 'Pushed'))
              .with('IN_PROGRESS', () =>
                pushIntegrationName ? `Push to ${pushIntegrationName} in progress` : 'Push in progress'
              )
              .with('FAILED', () => (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <span className={typography.extraSmall} style={{ color: mappedColors.neutral.neutral10 }}>
                    Push failed
                  </span>
                  {pushState?.error_reason && (
                    <span className={typography.extraSmall} style={{ color: mappedColors.neutral.neutral30 }}>
                      {pushState.error_reason}
                    </span>
                  )}
                </div>
              ))
              .otherwise(() =>
                pushIntegrationName ? `Push classification to ${pushIntegrationName}` : 'Push classification'
              )}
          </TooltipWithHover>
        )}
      </div>
    </div>
  )
}

const ScaCompletedBanner: React.FC<{
  outcome: CompletedScaOutcome
  copyText?: string
  copyButtonTooltip?: string
}> = ({ outcome, copyText, copyButtonTooltip }) => {
  const variant = getBannerVariant(outcome)
  const classificationLabel = getScaClassificationLabel(outcome.classification)
  const isExploitable = outcome.classification === 'exploitable'

  return (
    <div className={styles.banner({ variant })}>
      <div className={styles.metadataGroup}>
        <span className={styles.metadataLabel}>Classification</span>
        <span className={styles.metadataValue}>{classificationLabel}</span>
        {isExploitable && <ShieldWarningIcon className={styles.warningIcon} weight="light" />}
      </div>
      <div className={styles.copyButtonContainer}>
        <CopyButton
          copyText={copyText ?? ''}
          tooltipText={copyButtonTooltip ?? DEFAULT_COPY_LABEL}
          label={copyButtonTooltip ?? DEFAULT_COPY_LABEL}
          size="xlarge"
        />
      </div>
    </div>
  )
}

export const OutcomeBanner: React.FC<OutcomeBannerProps> = ({
  outcome,
  copyText,
  copyButtonTooltip,
  onPush,
  isPushInProgress,
  pushIntegrationName,
  pushState,
}) =>
  match(outcome)
    .with({ type: 'completed-triage' }, triageOutcome => (
      <TriageCompletedBanner
        outcome={triageOutcome}
        copyText={copyText}
        copyButtonTooltip={copyButtonTooltip}
        onPush={onPush}
        isPushInProgress={isPushInProgress}
        pushIntegrationName={pushIntegrationName}
        pushState={pushState}
      />
    ))
    .with({ type: 'completed-sca' }, scaOutcome => (
      <ScaCompletedBanner outcome={scaOutcome} copyText={copyText} copyButtonTooltip={copyButtonTooltip} />
    ))
    .with({ type: 'failed-triage' }, { type: 'failed-sca' }, () => (
      <ClassificationBanner
        label="Error"
        icon={<XIcon className={styles.errorIcon} weight="light" />}
        copyText={copyText}
        copyButtonTooltip={copyButtonTooltip}
      />
    ))
    .with({ type: 'blocked-triage' }, { type: 'blocked-sca' }, () => (
      <ClassificationBanner
        label="Blocked"
        icon={<ProhibitIcon className={styles.blockedIcon} weight="light" />}
        copyText={copyText}
        copyButtonTooltip={copyButtonTooltip}
      />
    ))
    .with({ type: 'excluded-triage' }, { type: 'excluded-sca' }, () => (
      <ClassificationBanner
        label="Excluded"
        icon={<MinusCircleIcon className={styles.excludedIcon} weight="light" />}
        copyText={copyText}
        copyButtonTooltip={copyButtonTooltip}
      />
    ))
    .with({ type: 'failed-fix' }, () => (
      <SimpleBanner label="Error" icon={<XIcon className={styles.errorIcon} weight="light" />} variant="void" />
    ))
    .with({ type: 'blocked-fix' }, () => (
      <SimpleBanner
        label="Blocked"
        icon={<ProhibitIcon className={styles.blockedIcon} weight="light" />}
        variant="void"
      />
    ))
    .with({ type: 'out-of-scope-fix' }, { type: 'excluded-fix' }, () => (
      <SimpleBanner
        label="Excluded"
        icon={<MinusCircleIcon className={styles.excludedIcon} weight="light" />}
        variant="void"
      />
    ))
    .with({ type: 'no-recommendations-triage' }, () => (
      <div className={styles.banner({ variant: 'nebula' })}>
        <div className={styles.metadataGroup}>
          <span className={styles.statusLabel}>No recommendations</span>
          <CheckIcon className={styles.successIcon} weight="light" />
        </div>
        <div className={styles.copyButtonContainer}>
          <CopyButton
            copyText={copyText ?? ''}
            tooltipText={copyButtonTooltip ?? DEFAULT_COPY_LABEL}
            label={copyButtonTooltip ?? DEFAULT_COPY_LABEL}
            size="xlarge"
          />
        </div>
      </div>
    ))
    .with({ type: 'no-recommendations-fix' }, () => (
      <SimpleBanner
        label="No fix necessary"
        icon={<CheckIcon className={styles.successIcon} weight="light" />}
        variant="nebula"
      />
    ))
    .exhaustive()
